import React, { useState } from 'react';
import { ShieldAlert, ChevronDown, ChevronUp, AlertTriangle, ShieldCheck, Zap } from 'lucide-react';

const RISK_CONFIG = {
  low: { color: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/10', label: 'Faible' },
  high: { color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/10', label: 'High' },
  critical: { color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/10', label: 'Critique' },
};

/**
 * Collapsible panel showing detected EDR products and bypass suggestions.
 *
 * Props:
 *   edrResults – { detected: string[], risk: string, suggestions: { [edr]: { chain, confidence, reasoning } } }
 *   visible    – show/hide
 */
const EDRDetectionPanel = ({ edrResults, visible = false }) => {
  const [expanded, setExpanded] = useState(true);

  if (!visible || !edrResults) return null;

  const { detected = [], risk = 'low', suggestions = {} } = edrResults;
  const riskCfg = RISK_CONFIG[risk] || RISK_CONFIG.low;

  return (
    <div className={`rounded-xl border ${riskCfg.border} ${riskCfg.bg} overflow-hidden mb-4`}>
      {/* Header */}
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <ShieldAlert className={`w-5 h-5 ${riskCfg.color}`} />
          <h3 className={`font-bold ${riskCfg.color}`}>
            EDR Detection — {detected.length} product{detected.length !== 1 ? 's' : ''} detected
          </h3>
          <span className={`px-2 py-0.5 rounded text-xs font-bold border ${riskCfg.border} ${riskCfg.color}`}>
            Risque {riskCfg.label}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </div>

      {/* Body */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {detected.length === 0 ? (
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <ShieldCheck className="w-4 h-4" />
              No EDR detected — clear path
            </div>
          ) : (
            detected.map((edr) => {
              const suggestion = suggestions[edr];
              return (
                <div
                  key={edr}
                  className="bg-black/30 rounded-lg p-3 border border-gray-700/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                      <span className="font-bold text-white text-sm">{edr}</span>
                    </div>
                    {suggestion?.confidence != null && (
                      <span className="text-xs px-2 py-0.5 rounded bg-brand-green/20 text-brand-green border border-brand-green/30">
                        Confiance: {suggestion.confidence}%
                      </span>
                    )}
                  </div>

                  {suggestion?.reasoning && (
                    <p className="text-xs text-gray-400 mb-2">{suggestion.reasoning}</p>
                  )}

                  {suggestion?.chain?.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Recommended bypass chain:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {suggestion.chain.map((step, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-300 border border-purple-500/30"
                          >
                            {step.tool || step.technique}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default EDRDetectionPanel;
